import React, { FC } from 'react';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export interface IDateTimeRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  startDateError?: boolean;
  endDateError?: boolean;
  startDateHelperText?: string;
  endDateHelperText?: string;
  className?: string;
  startDateDisabled?: boolean;
  endDateDisabled?: boolean;
  includeTime?: boolean;
  startDateLabel?: string;
  endDateLabel?: string;
  hasMinDate?: boolean;
}

export const DateTimeRangePicker: FC<IDateTimeRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDateError,
  endDateError,
  startDateHelperText,
  endDateHelperText,
  className,
  startDateDisabled,
  endDateDisabled,
  includeTime = true,
  startDateLabel,
  endDateLabel,
  hasMinDate = true,
}) => {
  const handleDateChange = (type: 'start' | 'end') => (newValue: Date | null) => {
    if (!newValue) return;

    if (type === 'start') {
      onStartDateChange(newValue);
    } else {
      onEndDateChange(newValue);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={`flex flex-wrap gap-2 mt-2 ${className}`}>
        <DateTimePicker
          views={includeTime ? ['year', 'month', 'day', 'hours'] : ['year', 'month', 'day']}
          format={includeTime ? 'MM/dd/yyyy hh:mm aaa' : 'MM/dd/yyyy'}
          label={startDateLabel || 'Start Date Time'}
          value={startDate}
          onChange={handleDateChange('start')}
          minDate={hasMinDate ? new Date() : undefined}
          slotProps={{ textField: { error: startDateError, helperText: startDateHelperText } }}
          disabled={startDateDisabled}
        />
        <DateTimePicker
          views={includeTime ? ['year', 'month', 'day', 'hours'] : ['year', 'month', 'day']}
          format={includeTime ? 'MM/dd/yyyy hh:mm aaa' : 'MM/dd/yyyy'}
          label={endDateLabel || 'End Date Time'}
          value={endDate}
          onChange={handleDateChange('end')}
          minDate={startDate || undefined}
          slotProps={{ textField: { error: endDateError, helperText: endDateHelperText } }}
          disabled={endDateDisabled}
        />
      </div>
    </LocalizationProvider>
  );
};
